import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Upload, CheckCircle, AlertCircle, History, RotateCcw, FileText,
    KeyRound, Eye, EyeOff, Copy, Check,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Projetos › Publicar versão — o que substitui o deploy
// ---------------------------------------------------------------------------
// O Sr. Marco gera o HTML como já faz hoje, envia por aqui e o link permanente
// passa a mostrar a versão nova. Sem commit, sem build, sem depender da dupla.
//
// A lista abaixo do envio é a rede de segurança: publicou o arquivo errado,
// restaura o anterior com um clique — o histórico guarda todas as versões.

const COR = '#7c3aed';
const COR_ESCURA = '#6d28d9';

const ProjetosEnvio = ({ onPublicado }) => {
    const [arquivo, setArquivo] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [aviso, setAviso] = useState(null); // { tipo: 'ok' | 'erro', texto }
    const [versoes, setVersoes] = useState([]);
    const [carregandoLista, setCarregandoLista] = useState(true);
    const inputRef = useRef(null);

    // Chave de publicação: buscada sob demanda, nunca junto com a tela.
    const [chave, setChave] = useState({
        valor: null, visivel: false, carregando: false, erro: null, copiado: false,
    });

    const carregarVersoes = useCallback(async () => {
        setCarregandoLista(true);
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/projetos/inventario/versoes', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!r.ok) throw new Error(`Falha ao carregar o histórico (${r.status})`);
            setVersoes(await r.json());
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e.message });
        } finally {
            setCarregandoLista(false);
        }
    }, []);

    useEffect(() => { carregarVersoes(); }, [carregarVersoes]);

    const publicar = async () => {
        if (!arquivo || enviando) return;
        setEnviando(true);
        setAviso(null);
        try {
            const token = localStorage.getItem('authToken');
            const form = new FormData();
            form.append('arquivo', arquivo);

            const r = await fetch('/api/projetos/inventario', {
                method: 'POST',
                // Sem Content-Type manual: o navegador precisa gerar o boundary
                // do multipart sozinho — informá-lo à mão quebra o upload.
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            const dados = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(dados.message || `Falha ao publicar (${r.status})`);

            setAviso({
                tipo: 'ok',
                texto: dados.versao
                    ? `Versão ${dados.versao} publicada. O link já mostra o conteúdo novo.`
                    : 'Versão publicada. O link já mostra o conteúdo novo.',
            });
            setArquivo(null);
            if (inputRef.current) inputRef.current.value = '';
            carregarVersoes();
            if (onPublicado) onPublicado();
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e.message });
        } finally {
            setEnviando(false);
        }
    };

    const restaurar = async (id, rotulo) => {
        if (!window.confirm(`Voltar o link permanente para a ${rotulo}?`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch(`/api/projetos/inventario/versoes/${id}/ativar`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
            });
            const dados = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(dados.message || `Falha ao restaurar (${r.status})`);
            setAviso({ tipo: 'ok', texto: `${rotulo} está no ar novamente.` });
            carregarVersoes();
            if (onPublicado) onPublicado();
        } catch (e) {
            setAviso({ tipo: 'erro', texto: e.message });
        }
    };

    const formatarData = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    const formatarTamanho = (bytes) => (bytes ? `${Math.round(bytes / 1024)} KB` : '—');

    // -----------------------------------------------------------------------
    // Chave de publicação — a entrega do segredo sem passar por Teams/e-mail
    // -----------------------------------------------------------------------
    // A chave só é buscada no servidor QUANDO ele clica em "Revelar". Carregá-la
    // junto com a tela deixaria o segredo na página em toda visita, inclusive
    // quando ele só veio publicar um arquivo.
    const revelarChave = async () => {
        if (chave.valor) { setChave((c) => ({ ...c, visivel: !c.visivel })); return; }
        setChave((c) => ({ ...c, carregando: true, erro: null }));
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/projetos/chave-publicacao', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const dados = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(dados.message || `Falha ao ler a chave (${r.status})`);

            if (!dados.configurada) {
                throw new Error(
                    'A publicação automática está desligada no servidor — não há chave configurada. '
                    + 'Fale com a equipe de dados.'
                );
            }
            setChave({ valor: dados.chave, visivel: true, carregando: false, erro: null, copiado: false });
        } catch (e) {
            setChave((c) => ({ ...c, carregando: false, erro: e.message }));
        }
    };

    const copiarChave = async () => {
        if (!chave.valor) return;
        try {
            await navigator.clipboard.writeText(chave.valor);
            setChave((c) => ({ ...c, copiado: true }));
            setTimeout(() => setChave((c) => ({ ...c, copiado: false })), 2500);
        } catch {
            setChave((c) => ({ ...c, erro: 'O navegador bloqueou a cópia. Selecione o texto e copie à mão.' }));
        }
    };

    return (
        <div>
            {/* ----- Envio ----- */}
            <div style={{
                padding: '28px', borderRadius: '16px', border: '1px solid #ede9fe',
                backgroundColor: '#faf5ff', marginBottom: '28px',
            }}>
                <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                    Publicar uma versão nova
                </h3>
                <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>
                    Escolha o arquivo <strong>.html</strong> do inventário. Ao publicar, o link permanente
                    passa a mostrar essa versão imediatamente — a anterior fica guardada no histórico.
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <label style={{
                        display: 'inline-flex', alignItems: 'center', gap: '9px',
                        padding: '11px 20px', borderRadius: '10px', cursor: 'pointer',
                        border: `2px solid ${COR}`, backgroundColor: '#fff', color: COR, fontWeight: 700,
                    }}>
                        <FileText size={18} />
                        {arquivo ? 'Trocar arquivo' : 'Escolher arquivo'}
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".html,.htm,text/html"
                            style={{ display: 'none' }}
                            onChange={(e) => { setArquivo(e.target.files?.[0] || null); setAviso(null); }}
                        />
                    </label>

                    {arquivo && (
                        <span style={{ fontSize: '14px', color: '#334155' }}>
                            <strong>{arquivo.name}</strong>
                            <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                                {' '}· {formatarTamanho(arquivo.size)}
                            </span>
                        </span>
                    )}

                    <button
                        onClick={publicar}
                        disabled={!arquivo || enviando}
                        style={{
                            marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '9px',
                            padding: '11px 24px', borderRadius: '10px', border: 'none', fontWeight: 700,
                            cursor: !arquivo || enviando ? 'not-allowed' : 'pointer',
                            backgroundColor: !arquivo || enviando ? '#cbd5e1' : COR,
                            color: '#fff', transition: 'background-color .2s',
                        }}
                    >
                        <Upload size={18} /> {enviando ? 'Publicando…' : 'Publicar'}
                    </button>
                </div>

                {aviso && (
                    <div style={{
                        marginTop: '18px', padding: '13px 16px', borderRadius: '10px',
                        display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px',
                        backgroundColor: aviso.tipo === 'ok' ? '#ecfdf5' : '#fef2f2',
                        border: `1px solid ${aviso.tipo === 'ok' ? '#a7f3d0' : '#fecaca'}`,
                        color: aviso.tipo === 'ok' ? '#065f46' : '#991b1b',
                    }}>
                        {aviso.tipo === 'ok'
                            ? <CheckCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
                            : <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />}
                        <span>{aviso.texto}</span>
                    </div>
                )}
            </div>

            {/* ----- Chave de publicação ----- */}
            {/* Existe para que a chave chegue ao processo publicador sem trafegar
                por chat, e-mail ou documento — ele lê daqui, autenticado. */}
            <div style={{
                padding: '22px 24px', borderRadius: '14px', border: '1px solid #e2e8f0',
                backgroundColor: '#fff', marginBottom: '28px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
                    <KeyRound size={18} color={COR_ESCURA} />
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                        Chave de publicação
                    </h3>
                </div>
                <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '13.5px', lineHeight: 1.6 }}>
                    Use esta chave no processo que publica o inventário sozinho, no cabeçalho{' '}
                    <code style={{
                        padding: '1px 6px', borderRadius: '5px', backgroundColor: '#f1f5f9',
                        color: '#334155', fontSize: '12.5px',
                    }}>X-API-Key</code>.
                    Ela <strong>só publica</strong> — não lê o inventário, não abre o histórico e não
                    dá acesso a nenhuma outra parte do portal. Trate como senha: não a envie por
                    mensagem, e-mail ou documento compartilhado.
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                    <code style={{
                        flex: '1 1 340px', minWidth: 0, padding: '11px 14px', borderRadius: '9px',
                        border: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
                        fontSize: '13px', color: chave.visivel ? '#0f172a' : '#94a3b8',
                        overflowWrap: 'anywhere', letterSpacing: chave.visivel ? 0 : '2px',
                    }}>
                        {chave.visivel && chave.valor ? chave.valor : '••••••••••••••••••••••••••••••••'}
                    </code>

                    <button
                        onClick={revelarChave}
                        disabled={chave.carregando}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '7px',
                            padding: '10px 18px', borderRadius: '9px', fontWeight: 700, fontSize: '13.5px',
                            cursor: chave.carregando ? 'wait' : 'pointer',
                            border: `2px solid ${COR}`, backgroundColor: '#fff', color: COR,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {chave.visivel ? <EyeOff size={16} /> : <Eye size={16} />}
                        {chave.carregando ? 'Lendo…' : chave.visivel ? 'Ocultar' : 'Revelar'}
                    </button>

                    {chave.valor && (
                        <button
                            onClick={copiarChave}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '7px',
                                padding: '10px 18px', borderRadius: '9px', border: 'none',
                                fontWeight: 700, fontSize: '13.5px', cursor: 'pointer',
                                backgroundColor: chave.copiado ? '#059669' : COR, color: '#fff',
                                whiteSpace: 'nowrap', transition: 'background-color .2s',
                            }}
                        >
                            {chave.copiado ? <Check size={16} /> : <Copy size={16} />}
                            {chave.copiado ? 'Copiada' : 'Copiar'}
                        </button>
                    )}
                </div>

                {chave.erro && (
                    <div style={{
                        marginTop: '14px', padding: '12px 15px', borderRadius: '9px',
                        display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13.5px',
                        backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    }}>
                        <AlertCircle size={17} style={{ flexShrink: 0, marginTop: '1px' }} />
                        <span>{chave.erro}</span>
                    </div>
                )}
            </div>

            {/* ----- Histórico ----- */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
                <History size={19} color={COR_ESCURA} />
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                    Histórico de publicações
                </h3>
            </div>

            {carregandoLista ? (
                <p style={{ color: '#64748b', fontSize: '14px' }}>Carregando…</p>
            ) : versoes.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: '14px' }}>Nenhuma versão publicada ainda.</p>
            ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: '640px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8fafc' }}>
                                {['Versão', 'Publicado em', 'Por', 'Tamanho', ''].map((h, i) => (
                                    <th key={i} style={{
                                        textAlign: i === 3 ? 'right' : 'left', padding: '11px 14px',
                                        fontWeight: 700, color: '#475569', borderBottom: '1px solid #e2e8f0',
                                        whiteSpace: 'nowrap',
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {versoes.map((v) => (
                                <tr key={v.id} style={{ backgroundColor: v.ativo ? '#faf5ff' : '#fff' }}>
                                    <td style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>
                                        <span style={{ fontWeight: 700, color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                                            {v.versao || 'sem carimbo'}
                                        </span>
                                        {v.ativo && (
                                            <span style={{
                                                marginLeft: '9px', padding: '3px 9px', borderRadius: '999px',
                                                backgroundColor: COR, color: '#fff', fontSize: '11px', fontWeight: 800,
                                            }}>NO AR</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatarData(v.dataEnvio)}
                                    </td>
                                    <td style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', color: '#475569' }}>
                                        {v.enviadoPor || '—'}
                                    </td>
                                    <td style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', color: '#64748b', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                        {formatarTamanho(v.tamanhoBytes)}
                                    </td>
                                    <td style={{ padding: '11px 14px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                                        {!v.ativo && (
                                            <button
                                                onClick={() => restaurar(v.id, `versão ${v.versao || `de ${formatarData(v.dataEnvio)}`}`)}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                    padding: '6px 13px', borderRadius: '8px', cursor: 'pointer',
                                                    border: '1px solid #ddd6fe', backgroundColor: '#fff',
                                                    color: COR_ESCURA, fontWeight: 600, fontSize: '13px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <RotateCcw size={14} /> Restaurar
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ProjetosEnvio;
